// Shared post-subscribe unlock transition (Option A). After a doesspress /
// email-list confirmation the reader's unlock token must ride a same-origin
// GET (?tpmu=…) so the worker can verify it, promote the tpm-post-unlock
// cookie, and ungate the article. All five widget call sites converge here.
//
// On gated articles that GET is a fetch(): the response is the fully unlocked
// render, so the article content region is swapped into the live DOM in place
// — no navigation, scroll and form state preserved, ?tpmu= never reaches the
// URL/history/referrer. Any failure in the swap chain falls back to a plain
// location.replace reload, which is also the whole story for non-gated pages
// (rows 4/5).
//
// Decision table:
// 1. confirmation.key === "list_pref_create_account"  → no-op
// 2. no requestData.addToMultipleLists               → no-op
// 3. token and page is gated (.entry-content .NewsletterPaywall)
//    → fetch + in-place swap; fallback = reload with ?tpmu=
// 4. token, page not gated (or URL already has tpmu — C+ loop guard)
//    → reload with ?tpmu=
// 5. no token                                        → bare reload

import { reinitContent } from "tpm-lib/content-reinit.js";

// The unlock fetch is a same-document background request; give it up to 20s
// before falling back to the reload.
const FETCH_TIMEOUT_MS = 20000;

// The reload fallback: location.replace keeps ?tpmu= out of the history
// stack. Used by rows 4/5 and every swap failure path.
function navigateFallback(url) {
	try {
		window.location.replace(url);
	} catch (e) {
		console.warn("post-unlock: fallback navigation failed", e);
	}
}

// Product decision: no ads in swapped content. Strip both placeholder forms
// (div.x-placeholder and the legacy script[type=x-placeholder]) from the
// fetched fragment before it enters the DOM. The live teaser's p4 ad dies
// with the old .entry-content too — accepted (impression was served at load).
function stripAdPlaceholders(root) {
	root
		.querySelectorAll(
			'.x-placeholder[data-widget="AdSlot"], script[type="x-placeholder"][data-widget="AdSlot"]'
		)
		.forEach((el) => el.remove());
}

// Module-level idempotence guard: mirrors C+'s per-call `navigated`. Once a
// swap has landed, repeated confirmations (double-submit, second subscribe
// without navigating) must not re-fetch or re-swap.
let swapped = false;

// Test-only: the idempotence guard is module state; tests reset it between
// cases so one test's landed swap doesn't silence the next.
export function __resetForTests() {
	swapped = false;
}

async function swapUnlocked(token) {
	const loc = window.location;
	// Any same-origin GET carrying ?tpmu= both promotes the tpm-post-unlock
	// cookie (Set-Cookie on the fetch response) and returns the unlocked
	// render — this URL doubles as the fallback navigation target.
	const url = loc.origin + loc.pathname + "?tpmu=" + encodeURIComponent(token);

	const abort = new AbortController();
	const timeout = setTimeout(() => abort.abort(), FETCH_TIMEOUT_MS);
	let res;
	try {
		// credentials are mandatory: the worker's unlock gate requires the
		// tpm-auth-token session cookie on this GET.
		res = await fetch(url, {
			credentials: "same-origin",
			signal: abort.signal,
		});
	} catch (e) {
		clearTimeout(timeout);
		navigateFallback(url);
		return;
	}
	clearTimeout(timeout);

	if (!res.ok) {
		navigateFallback(url);
		return;
	}

	let fetchedDoc;
	try {
		fetchedDoc = new DOMParser().parseFromString(await res.text(), "text/html");
	} catch (e) {
		navigateFallback(url);
		return;
	}

	const fetchedEntry = fetchedDoc.querySelector(".entry-content");
	const liveEntry = document.querySelector(".entry-content");
	// Guards: the fetched render must have a content region, and it must no
	// longer be gated — a paywall box in the fragment means the unlock didn't
	// verify (e.g. cold-isolate key-fetch failure); swapping would just
	// replace a gated form with a gated form.
	if (
		!fetchedEntry ||
		!liveEntry ||
		fetchedEntry.querySelector(".NewsletterPaywall")
	) {
		navigateFallback(url);
		return;
	}

	try {
		stripAdPlaceholders(fetchedEntry);
		liveEntry.replaceWith(fetchedEntry);
		swapped = true;

		// The paywall UI and bumper/modal widgets key on this flag.
		window.has_paywall = false;
		// The fetch already promoted the cookie, so the URL params are safe to
		// scrub (magic-link landing edge: gated render + fresh token).
		const params = new URLSearchParams(loc.search);
		if (params.has("tpmu") || params.has("addToMultipleLists")) {
			history.replaceState(null, "", loc.pathname);
		}

		try {
			window._widgets?.initSubtree?.(fetchedEntry);
			reinitContent(fetchedEntry);
		} catch (e) {
			// Content is already swapped — a re-init throw must not strand the
			// reader. The page is usable; worst case an embed is unstyled.
			console.warn("post-unlock: re-init after content swap failed", e);
		}
	} catch (e) {
		navigateFallback(url);
	}
}

export function postSubscribeUnlock(confirmation, requestData) {
	if (!confirmation) return;
	if (confirmation.key === "list_pref_create_account") return;
	if (!requestData || !requestData.addToMultipleLists) return;
	if (swapped) return; // idempotent: a landed swap makes later confirmations no-ops

	const loc = window.location;
	const token = confirmation.unlockToken;
	// Gate detection is a DOM check: only a gated singular renders the
	// paywall part inside .entry-content. /newsletters and ungated posts
	// never enter the swap path.
	const isGated = !!document.querySelector(".entry-content .NewsletterPaywall");

	if (token && isGated) {
		// Background fetch + in-place swap; swapUnlocked falls back to
		// navigateFallback internally on any failure.
		swapUnlocked(token);
		return;
	}

	// Rows 4/5: reload fallback (including the URL-already-has-tpmu loop
	// guard, which is irrelevant on the swap path but still right here).
	const hasToken = !!token && !new URLSearchParams(loc.search).has("tpmu");
	navigateFallback(
		hasToken
			? loc.origin + loc.pathname + "?tpmu=" + encodeURIComponent(token)
			: loc.origin + loc.pathname
	);
}
